import { splitEvery } from 'ramda'
import {
  addDays as addDaysFn,
  addMonths as addMonthsFn,
  eachDay as eachDayFn,
  format as formatFn,
  getDate as getDateFn,
  getDay as getDayFn,
  getMonth as getMonthFn,
  getYear as getYearFn,
  isAfter as isAfterFn,
  isBefore as isBeforeFn,
  isSameDay as isSameDayFn,
  isSameMonth as isSameMonthFn,
  isToday as isTodayFn,
  isWithinRange as isWithinRangeFn,
  startOfMonth as startOfMonthFn,
  subDays as subDaysFn,
  subMonths as subMonthsFn,
} from 'date-fns'

// rows in calendar
const CALENDAR_WEEKS: number = 6

const DAYS_IN_WEEK: number = 7

export const DAYS_IN_CALENDAR: number = CALENDAR_WEEKS * DAYS_IN_WEEK

export const addDays: (date: Date, num: number) => Date = addDaysFn

export const addMonths: (date: Date, num: number) => Date = addMonthsFn

export const eachDay: (date1: Date, date2: Date) => Date[] = eachDayFn

export const format: (date: Date, formatString: string) => string = formatFn

// An entire date object has to be formed, format(month, 'MMM') breaks for month >= 10 (it just outputs 'Dec')
export const formatMonth: (month: number) => string = month =>
  formatFn(normalizeNewDate(2019, month, 2), 'MMM')

export const getDate: (date: Date) => number = getDateFn

export const getDates: (date: Date) => Date[] = date => {
  const start = subDaysFn(startOfMonthFn(date), getDayFn(startOfMonthFn(date)))
  const end = addDaysFn(start, DAYS_IN_CALENDAR - 1)

  return eachDayFn(start, end)
}

export const getMonth: (date: Date) => number = getMonthFn

export const getRows: (dates: Date[]) => Date[][] = splitEvery(7)

export const getYear: (date: Date) => number = getYearFn

export const isAfter: (date1: Date, date2: Date) => boolean = (date1, date2) =>
  isAfterFn(normalizeExistingDate(date1), normalizeExistingDate(date2))

export const isBefore: (date1: Date, date2: Date) => boolean = (date1, date2) =>
  isBeforeFn(normalizeExistingDate(date1), normalizeExistingDate(date2))

export const isSameDay: (date1: Date, date2: Date) => boolean = (
  date1,
  date2
) => isSameDayFn(normalizeExistingDate(date1), normalizeExistingDate(date2))

export const isSameMonth: (date1: Date, date2: Date) => boolean = isSameMonthFn

export const isToday: (date: Date) => boolean = isTodayFn

export const isWithinRange: (
  date: Date,
  start?: Date,
  end?: Date
) => boolean = (date, start, end) => {
  if (!start || !end) {
    return false
  }

  return isWithinRangeFn(
    normalizeExistingDate(date),
    normalizeExistingDate(start),
    normalizeExistingDate(end)
  )
}

export const normalizeExistingDate: (day: Date) => Date = day =>
  normalizeNewDate(getYearFn(day), getMonthFn(day), getDateFn(day))

export const normalizeNewDate: (
  year: number,
  month: number,
  day: number,
  hour?: number,
  minute?: number,
  second?: number,
  millisecond?: number
) => Date = (
  year,
  month,
  day,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0
) => new Date(year, month, day, hour, minute, second, millisecond)

export const subDays: (date: Date, num: number) => Date = subDaysFn

export const subMonths: (date: Date, num: number) => Date = subMonthsFn

const CalendarHelpers = {
  addDays,
  addMonths,
  eachDay,
  format,
  formatMonth,
  getDate,
  getDates,
  getMonth,
  getRows,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinRange,
  normalizeExistingDate,
  normalizeNewDate,
  subDays,
  subMonths,
}

export default CalendarHelpers
